// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useNavigate } from '@tanstack/react-router';

// material ui icons
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';

// material ui
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

// components
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';

const SavedLinks = ({ drawerOpen = true, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [savedLinks, setSavedLinks] = useLocalStorageJson('savedLinks', []);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const { t } = useTranslation('navigation_favorites');

  // Ensure savedLinks is always an array
  const links = useMemo(
    () => (Array.isArray(savedLinks) ? savedLinks : []),
    [savedLinks],
  );

  const handleAdd = () => {
    const currentPath = location.pathname + location.searchStr;
    const newLink = {
      id: Date.now(),
      path: currentPath,
      label: t('defaultLabel', { number: links.length + 1 }),
    };
    setSavedLinks([...links, newLink]);
    setEditingId(newLink.id);
    setEditValue(newLink.label);
  };

  const handleLinkClick = (path) => {
    navigate({ to: path });
    onNavigate?.(); // useful for closing mobile drawer
  };

  const handleDelete = (id) => {
    setSavedLinks(links.filter((link) => link.id !== id));
  };

  const handleStartEdit = (id, currentLabel) => {
    setEditingId(id);
    setEditValue(currentLabel);
  };

  const handleSaveEdit = (id) => {
    setSavedLinks(
      links.map((link) =>
        link.id === id ? { ...link, label: editValue } : link,
      ),
    );
    setEditingId(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  // ----------------------------
  // Collapsed (mini-drawer) UI
  // ----------------------------
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => {
    setAnchorEl(null);
    // If a user collapses then reopens menu, keep edit state sane
    setEditingId(null);
    setEditValue('');
  };

  if (!drawerOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Tooltip
          title={t('title', { defaultValue: 'Favorites' })}
          placement="right"
        >
          <IconButton
            onClick={openMenu}
            aria-label="Open favorites"
            aria-controls={menuOpen ? 'saved-links-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            sx={{
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[1000],
            }}
          >
            <StarBorderRoundedIcon />
          </IconButton>
        </Tooltip>

        <Menu
          id="saved-links-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 250 } } }}
        >
          <Box sx={{ px: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarBorderRoundedIcon fontSize="small" />
              <Box sx={{ fontWeight: 700 }}>
                {t('title', { defaultValue: 'Favorites' })}
              </Box>

              <Box sx={{ flex: 1 }} />

              {/* <Tooltip
                title={t('addButtonTooltip')}
                placement="top"
                disableInteractive
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    handleAdd();
                    // keep menu open; users may want to edit right away
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip> */}
            </Box>
          </Box>

          <Divider />

          {links.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary={t('empty', { defaultValue: 'No saved links yet.' })}
              />
            </MenuItem>
          ) : (
            links.map((link) => (
              <Box key={link.id} sx={{ px: 1, pt: 1 }}>
                {editingId === link.id ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <TextField
                      key={`edit-${link.id}`}
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                      fullWidth
                    />
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSaveEdit(link.id)}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <MenuItem
                    sx={{ borderRadius: 1.5 }}
                    onClick={() => {
                      handleLinkClick(link.path);
                      closeMenu();
                    }}
                  >
                    <ListItemText
                      primary={link.label}
                      slotProps={{
                        primary: {
                          noWrap: true,
                          title: link.label,
                        }
                      }}
                    />
                    <ListItemIcon sx={{ minWidth: 0, ml: 1 }}>
                      <Tooltip
                        title={t('edit', { defaultValue: 'Edit' })}
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(link.id, link.label);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 0, ml: 0.5 }}>
                      <Tooltip
                        title={t('delete', { defaultValue: 'Delete' })}
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(link.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemIcon>
                  </MenuItem>
                )}
              </Box>
            ))
          )}

          <Divider sx={{ my: 1 }} />

          <Box sx={{ px: 1.5, pb: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              sx={{ borderStyle: 'dashed' }}
              onClick={() => {
                handleAdd();
              }}
            >
              {t('addButtonText')}
            </Button>
          </Box>
        </Menu>
      </Box>
    );
  }

  // ----------------------------
  // Expanded drawer UI (your current version)
  // ----------------------------
  return (
    <Box mb={2}>
      <Stack
        spacing={1}
        sx={{
          maxHeight: links.length > 5 ? '300px' : 'auto',
          overflowY: links.length > 5 ? 'auto' : 'visible',
          mb: 1,
        }}
      >
        {links.map((link) => (
          <Box
            key={link.id}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
              '&:hover .action-icons': { opacity: 1 },
            }}
          >
            {editingId === link.id ? (
              <>
                <TextField
                  key={`edit-${link.id}`}
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  fullWidth
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleSaveEdit(link.id)}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={handleCancelEdit}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  variant="text"
                  onClick={() => handleLinkClick(link.path)}
                  sx={{
                    flex: 1,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  <Tooltip
                    title={link.label}
                    placement="top"
                    disableInteractive
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {link.label}
                    </Box>
                  </Tooltip>
                </Button>
                <Box
                  className="action-icons"
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartEdit(link.id, link.label);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(link.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        ))}
      </Stack>

      <Tooltip
        title={t('addButtonTooltip')}
        placement="bottom"
        disableInteractive
      >
        <Button
          variant="outlined"
          onClick={handleAdd}
          fullWidth
          startIcon={<AddIcon />}
          sx={{ borderStyle: 'dashed' }}
        >
          {t('addButtonText')}
        </Button>
      </Tooltip>
    </Box>
  );
};

export default SavedLinks;
